import React, { FC, useEffect, useRef, useState } from 'react';

import Router from 'next/router';

import {
    acceptConsent,
    declineConsent,
    getConsentDecision,
    initializeConsent,
    onCookieSettingsOpen,
    subscribeConsent,
} from '@utils/cookie-consent';
import { configureAnalytics, trackPageView } from '@utils/metrika';

import { CookieConsentView, getCookieBannerText } from './CookieConsentView';

type CookieConsentProps = {
    text?: string | null;
    counterId?: number | null;
};

export const CookieConsent: FC<CookieConsentProps> = (props) => {
    const { text, counterId } = props;
    const [isInitialized, setInitialized] = useState(false);
    const [isSettingsOpen, setSettingsOpen] = useState(false);
    const [decision, setDecision] = useState(getConsentDecision);
    const panelRef = useRef<HTMLElement>(null);
    const isVisible = isInitialized && (isSettingsOpen || decision === null);

    useEffect(() => {
        initializeConsent();
        setDecision(getConsentDecision());
        setInitialized(true);

        return subscribeConsent((nextDecision) => {
            setDecision(nextDecision);
            if (nextDecision !== null) setSettingsOpen(false);
        });
    }, []);

    useEffect(
        () =>
            onCookieSettingsOpen(() => {
                setDecision(getConsentDecision());
                setSettingsOpen(true);
            }),
        [],
    );

    useEffect(() => {
        configureAnalytics(counterId);
    }, [counterId]);

    useEffect(() => {
        if (!isVisible) return undefined;

        /* The fixed banner owns the bottom-right corner while it is open. Keep
           the scroll-to-top control out of the way without observing layout. */
        document.body.dataset.cookieBannerVisible = 'true';
        return () => {
            delete document.body.dataset.cookieBannerVisible;
        };
    }, [isVisible]);

    useEffect(() => {
        Router.events.on('routeChangeComplete', trackPageView);
        return () => Router.events.off('routeChangeComplete', trackPageView);
    }, []);

    useEffect(() => {
        if (!isSettingsOpen) return;
        panelRef.current?.focus();
    }, [isSettingsOpen]);

    if (!isInitialized) return null;

    if (!isVisible) return null;

    return (
        <CookieConsentView
            text={getCookieBannerText(text)}
            panelRef={panelRef}
            onAccept={() => {
                setSettingsOpen(false);
                acceptConsent();
            }}
            onDecline={() => {
                setSettingsOpen(false);
                declineConsent();
            }}
        />
    );
};
